import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { Logo } from "../components/common/Logo";

export const TermsPage: React.FC = () => {
  const { isDark } = useTheme();

  return (
    <div className={`min-h-screen ${isDark ? 'bg-black text-white' : 'bg-white text-black'} transition-colors duration-300`}>
      {/* Header */}
      <div className={`border-b ${isDark ? 'border-white/10' : 'border-black/10'}`}>
        <div className="max-w-4xl mx-auto px-4 py-6">
          <Link
            to="/"
            className={`inline-flex items-center gap-2 ${isDark ? 'text-white/60 hover:text-white' : 'text-black/60 hover:text-black'} transition-colors`}
          >
            <ArrowLeft size={20} />
            Back to Home
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <h1 className={`text-4xl font-black ${isDark ? 'text-white' : 'text-black'} mb-2`}>Terms of Service</h1>
        <p className={`${isDark ? 'text-white/60' : 'text-black/60'} mb-12`}>Last updated: November 27, 2024</p>

        <div className={`space-y-8 ${isDark ? 'text-white/80' : 'text-black/80'}`}>
          <section>
            <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-black'} mb-4`}>1. Acceptance of Terms</h2>
            <p className="leading-relaxed">
              By accessing or using ParlayGround ("the Service"), you agree to be bound by these Terms of Service.
              If you do not agree to these terms, please do not use our Service. We reserve the right to modify
              these terms at any time, and your continued use of the Service constitutes acceptance of any changes.
            </p>
          </section>

          <section>
            <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-black'} mb-4`}>2. Description of Service</h2>
            <p className="leading-relaxed mb-4">
              ParlayGround is a social community platform for sports fans. Our Service includes:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>SweatRooms - Live chat rooms for real-time game discussions</li>
              <li>GroundTokens (GT) - Virtual currency for in-app engagement and rewards</li>
              <li>Slip sharing and community interactions</li>
              <li>Trivia games and community challenges</li>
              <li>User profiles and social features</li>
            </ul>
          </section>

          <section>
            <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-black'} mb-4`}>3. Important Disclaimer</h2>
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6">
              <p className="leading-relaxed font-medium text-red-300">
                ParlayGround is NOT a sportsbook, gambling platform, or betting service. We do not facilitate,
                encourage, or process any real-money gambling or sports betting. GroundTokens (GT) have no
                real-world monetary value and cannot be exchanged for cash, cryptocurrency, or any form of
                real currency. GT are purely for entertainment and engagement within the ParlayGround community.
              </p>
            </div>
          </section>

          <section>
            <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-black'} mb-4`}>4. User Accounts</h2>
            <p className="leading-relaxed mb-4">To use certain features of the Service, you must create an account. You agree to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Provide accurate and complete information during registration</li>
              <li>Maintain the security of your account credentials</li>
              <li>Be at least 18 years old or the age of majority in your jurisdiction</li>
              <li>Not create multiple accounts or share your account with others</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
            </ul>
          </section>

          <section>
            <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-black'} mb-4`}>5. GroundTokens (GT)</h2>
            <p className="leading-relaxed mb-4">GroundTokens are virtual tokens used within ParlayGround:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>GT can be earned through community participation, promotions, and purchases</li>
              <li>GT can be used for tipping other users, entering contests, and unlocking features</li>
              <li>GT have no cash value and are non-refundable</li>
              <li>GT cannot be transferred outside of the platform</li>
              <li>We reserve the right to modify GT values, earning rates, and redemption options</li>
              <li>Unused GT may expire as specified in our policies</li>
            </ul>
          </section>

          <section>
            <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-black'} mb-4`}>6. User Conduct</h2>
            <p className="leading-relaxed mb-4">You agree not to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Post harmful, abusive, harassing, or discriminatory content</li>
              <li>Impersonate others or misrepresent your identity</li>
              <li>Spam, advertise, or promote external services without permission</li>
              <li>Attempt to manipulate GT balances or exploit system vulnerabilities</li>
              <li>Share illegal content or promote illegal gambling activities</li>
              <li>Violate any applicable laws or regulations</li>
              <li>Interfere with the proper functioning of the Service</li>
            </ul>
          </section>

          <section>
            <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-black'} mb-4`}>7. Content Ownership</h2>
            <p className="leading-relaxed">
              You retain ownership of content you create on ParlayGround. However, by posting content, you grant
              us a non-exclusive, worldwide, royalty-free license to use, display, and distribute your content
              within the Service. We reserve the right to remove any content that violates these terms.
            </p>
          </section>

          <section>
            <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-black'} mb-4`}>8. Account Termination</h2>
            <p className="leading-relaxed">
              We may suspend or terminate your account at our discretion for violations of these terms,
              fraudulent activity, or any behavior that harms the community. Upon termination, your access
              to the Service will be revoked and any accumulated GT will be forfeited.
            </p>
          </section>

          <section>
            <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-black'} mb-4`}>9. Limitation of Liability</h2>
            <p className="leading-relaxed">
              The Service is provided "as is" without warranties of any kind. We are not liable for any
              indirect, incidental, or consequential damages arising from your use of the Service. Our
              total liability shall not exceed the amount you have paid us in the past twelve months.
            </p>
          </section>

          <section>
            <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-black'} mb-4`}>10. Changes to Service</h2>
            <p className="leading-relaxed">
              We reserve the right to modify, suspend, or discontinue any part of the Service at any time
              without prior notice. We will not be liable to you or any third party for any modification,
              suspension, or discontinuation of the Service.
            </p>
          </section>

          <section>
            <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-black'} mb-4`}>11. Contact Us</h2>
            <p className="leading-relaxed">
              If you have any questions about these Terms of Service, please contact us at{" "}
              <a href="mailto:support@parlayground.com" className="text-primary hover:underline">
                support@parlayground.com
              </a>
            </p>
          </section>
        </div>

        {/* Footer */}
        <div className={`mt-16 pt-8 border-t ${isDark ? 'border-white/10' : 'border-black/10'}`}>
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <Logo size="sm" showText={true} />
            </Link>
          </div>
          <p className={`${isDark ? 'text-white/40' : 'text-black/40'} text-sm mt-4`}>
            © {new Date().getFullYear()} ParlayGround. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
